import { Loader2 } from "lucide-react";
import type { CarouselPropagateProgress } from "@/lib/api";
import { clockLabel, elapsedSec, useNowTick } from "@/lib/useNowTick";
import { cn } from "@/lib/utils";

const DEFAULT_MS = 12_000;

export default function PropagateJobProgress({
  progress,
  running,
  className,
}: {
  progress?: CarouselPropagateProgress | null;
  running?: boolean;
  className?: string;
}) {
  const phase = progress?.phase || (running ? "waiting" : "");
  const active = Boolean(running) && phase !== "done" && phase !== "failed" && phase !== "error";
  const now = useNowTick(active || Boolean(running && progress?.started_at));
  if (!progress?.started_at && !running) return null;

  const startedAt = progress?.started_at || "";
  const expectedMs = Math.max(
    2500,
    Number(progress?.expected_ms_per_slide || DEFAULT_MS),
  );
  const total = Math.max(1, Number(progress?.total || 1));
  const done = Math.max(0, Math.min(Number(progress?.done || 0), total));
  const current = Math.min(total, Math.max(1, Number(progress?.current || done + 1)));
  const jobMs = Number(progress?.job_expected_ms) || expectedMs * total;
  const elapsed = elapsedSec(startedAt, now);
  const etaSec = active ? Math.max(0, Math.round(jobMs / 1000) - elapsed) : 0;
  const expectedSec = Math.max(1, Math.round(jobMs / 1000));
  const samples = Number(progress?.samples || 0);
  const serverPct = Number(progress?.percent || 0);
  const timePct = Math.min(99, Math.max(1, Math.round((elapsed * 1000 * 100) / Math.max(1, jobMs))));
  const pct = phase === "done" ? 100 : Math.max(serverPct, timePct, Math.round((100 * done) / total));
  const title =
    phase === "done"
      ? "Propagacja gotowa"
      : phase === "failed" || phase === "error"
        ? "Propagacja przerwana"
        : progress?.detail || `Składam slajd ${current}/${total}`;

  return (
    <div
      className={cn("rounded-md border border-border bg-muted/30 p-3", className)}
      aria-live="polite"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 font-medium">
          {active ? <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" /> : null}
          {active ? `${done}/${total} · ` : null}
          {title}
        </span>
        <span className="tabular-nums text-muted-foreground">{pct}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
      </div>
      {startedAt ? (
        <p className="mt-2 text-xs text-muted-foreground">
          minęło {clockLabel(elapsed)}
          {active ? ` · zostało ok. ${clockLabel(etaSec)}` : ""}
          {active ? ` · średnio ~${clockLabel(expectedSec)}` : ""}
          {samples > 0
            ? ` · średnia z ${samples} ${samples === 1 ? "składu" : "składów"}`
            : ""}
        </p>
      ) : null}
    </div>
  );
}
